/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { router } from "@smakecloud/api-client-helper";
import { z } from "zod";
import { authedEndpoint } from "~/lib/api/builder";

export const dataSchema = z.object({
    id: z.number(),
    activated_at: z.string().nullable(),
    available_export_file_formats: z
        .array(
            z.object({
                name: z.string(),
                display_name: z.string(),
            }),
        )
        .optional(),
    created_at: z.string(),
    export_file_format: z.string().nullable(),
    has_presorting: z.boolean(),
    is_using_presorting: z.boolean(),
    production_id: z.number(),
    production_method: z.object({
        id: z.number(),
        description: z.string().nullable(),
        display_name: z.string().nullable(),
        information_digitalizer: z.string().nullable(),
        information_producer: z.string().nullable(),
        is_available: z.boolean(),
        is_digitizable: z.boolean(),
        production_areas_count: z.number().nullable(),
        production_type: z.object({
            id: z.number(),
            name: z.string().nullable(),
        }),
    }),
    production_method_handle: z.string(),
    updated_at: z.string(),
});

export const productionMethodsSchema = z.array(dataSchema);

export type productionMethods = z.infer<typeof productionMethodsSchema>;

const productionProductionMethodSchema = z.object({
    data: dataSchema,
    dtf_print_lock: z.boolean().nullable(),
});

const list = authedEndpoint.query(
    (searchParameters) => ({
        searchParams: searchParameters,
        url: "production-methods",
    }),
    {
        input: z
            .object({
                cursor: z.string(),
                filter: z
                    .object({
                        q: z.string(),
                    })
                    .partial(),
                per_page: z.number(),
            })
            .partial()
            .optional(),
        output: z.object({
            data: productionMethodsSchema,
            links: z.object({
                first: z.string().nullable(),
                last: z.string().nullable(),
                next: z.string().nullable(),
                prev: z.string().nullable(),
            }),
            meta: z.object({
                next_cursor: z.string().nullable(),
                path: z.string(),
                per_page: z.number(),
                prev_cursor: z.string().nullable(),
            }),
        }),
    },
);

const get = authedEndpoint.query(
    (id) => ({
        url: `production-methods/${id}`,
    }),
    {
        input: z.number(),
        output: productionProductionMethodSchema,
    },
);

const activatePresorting = authedEndpoint.mutation(
    (id) => ({
        method: "POST",
        url: `production-methods/${id}/presort/activate`,
    }),
    {
        input: z.number(),
        output: dataSchema,
    },
);

const deactivatePresorting = authedEndpoint.mutation(
    (id) => ({
        method: "POST",
        url: `production-methods/${id}/presort/deactivate`,
    }),
    {
        input: z.number(),
        output: dataSchema,
    },
);

const activateMethod = authedEndpoint.mutation(
    (id) => ({
        method: "PATCH",
        url: `production-methods/${id}/activate`,
    }),
    {
        input: z.number(),
        output: dataSchema,
    },
);

const deactivateMethod = authedEndpoint.mutation(
    (id) => ({
        method: "PATCH",
        url: `production-methods/${id}/deactivate`,
    }),
    {
        input: z.number(),
        output: dataSchema,
    },
);

const unlockPrint = authedEndpoint.mutation(
    () => ({
        method: "POST",
        url: `production-methods/dtf/print/reset`,
    }),
    {
        input: z.undefined(),
        output: z.object({
            message: z.string(),
        }),
    },
);

import { sortingRacks } from "./sortingRacks";

export const productionMethods = router({
    activateMethod,
    activatePresorting,
    deactivateMethod,
    deactivatePresorting,
    get,
    list,
    // Sub-Endpoints
    sortingRacks,
    unlockPrint,
});
